import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ADMIN_API } from "../common/common";
import { postApi, notifyError } from "../service";
import { getTheme } from "../common/StyleSeter";

type Signature = (number | null)[];

const IMAGE_TYPE_REGISTRY: Record<
  string,
  { extensions: string[]; signatures: Signature[] }
> = {
  "image/jpeg": {
    extensions: ["jpg", "jpeg", "jpe", "jfif"],
    signatures: [[0xff, 0xd8, 0xff]],
  },
  "image/png": {
    extensions: ["png"],
    signatures: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  },
  "image/gif": {
    extensions: ["gif"],
    signatures: [
      [0x47, 0x49, 0x46, 0x38, 0x37, 0x61],
      [0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
    ],
  },
  "image/webp": {
    extensions: ["webp"],
    signatures: [
      [
        0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50,
      ],
    ],
  },
  "image/bmp": { extensions: ["bmp"], signatures: [[0x42, 0x4d]] },
  "image/avif": {
    extensions: ["avif"],
    signatures: [
      [null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x61, 0x76, 0x69, 0x66],
      [null, null, null, null, 0x66, 0x74, 0x79, 0x70, 0x61, 0x76, 0x69, 0x73],
    ],
  },
  "image/tiff": {
    extensions: ["tiff", "tif"],
    signatures: [
      [0x49, 0x49, 0x2a, 0x00],
      [0x4d, 0x4d, 0x00, 0x2a],
    ],
  },
  "image/x-icon": {
    extensions: ["ico"],
    signatures: [[0x00, 0x00, 0x01, 0x00]],
  },
};

const DEFAULT_ALLOWED_MIME_TYPES = Object.keys(IMAGE_TYPE_REGISTRY);
const DEFAULT_MAX_FILE_SIZE_MB = 3;

const normalizeMimeType = (type: string) => {
  if (type === "image/jpg") return "image/jpeg";
  if (type === "image/x-icon/ico" || type === "image/vnd.microsoft.icon") {
    return "image/x-icon";
  }
  return type;
};

const getExtension = (filename: string) => {
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
};

const findTypeByExtension = (ext: string) =>
  DEFAULT_ALLOWED_MIME_TYPES.find((type) =>
    IMAGE_TYPE_REGISTRY[type].extensions.includes(ext),
  );

const readFileHeader = (file: File, length = 12): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, length));
  });

const matchesSignature = (bytes: Uint8Array, signatures: Signature[]) =>
  signatures.some(
    (sig) =>
      bytes.length >= sig.length &&
      sig.every((byte, i) => byte === null || bytes[i] === byte),
  );

interface ValidationResult {
  error: string | null;
}

const validateImageFile = async (
  file: File,
  allowedMimeTypes: string[],
  maxFileSize: number,
): Promise<ValidationResult> => {
  if (file.size === 0) {
    return { error: "File is empty." };
  }

  if (file.size > maxFileSize * 1024 * 1024) {
    return {
      error: `File is too large. Maximum allowed size is ${maxFileSize}MB.`,
    };
  }

  const ext = getExtension(file.name);
  const normalizedType = normalizeMimeType(file.type);

  const resolvedType = IMAGE_TYPE_REGISTRY[normalizedType]
    ? normalizedType
    : normalizedType === ""
      ? findTypeByExtension(ext)
      : undefined;
  const config = resolvedType ? IMAGE_TYPE_REGISTRY[resolvedType] : undefined;

  if (!resolvedType || !config || !allowedMimeTypes.includes(resolvedType)) {
    return {
      error: `Unsupported format. Allowed: ${allowedMimeTypes
        .map((t) => t.replace("image/", ""))
        .join(", ")}`,
    };
  }

  if (!config.extensions.includes(ext)) {
    return {
      error:
        "File extension doesn't match its content — please re-select the file.",
    };
  }

  let header: Uint8Array;
  try {
    header = await readFileHeader(file);
  } catch (_) {
    return { error: "Could not read the file. Please try again." };
  }

  if (!matchesSignature(header, config.signatures)) {
    return { error: "This file doesn't appear to be a valid image." };
  }

  return { error: null };
};

const ImageUpload = (props: any) => {
  const {
    label,
    filename,
    setFileName,
    name,
    topClass,
    allowClear,
    domain,
    validateImage = true,
    maxFileSize = DEFAULT_MAX_FILE_SIZE_MB,
    allowedTypes,
  } = props;

  const DD = useSelector((e: any) => e.domainDetails);
  const themeColor = getTheme(DD?.colorSchema)?.backgroundColor;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const [previewFailed, setPreviewFailed] = useState(false);

  const effectiveMaxFileSize =
    Number(maxFileSize) > 0 ? Number(maxFileSize) : DEFAULT_MAX_FILE_SIZE_MB;

  const effectiveAllowedTypes: string[] =
    allowedTypes && allowedTypes.length > 0
      ? Array.from(
          new Set<string>(allowedTypes.map((t: string) => normalizeMimeType(t))),
        )
      : DEFAULT_ALLOWED_MIME_TYPES;

  const resolveDisplayUrl = (value: string) => {
    if (!value) return "";
    return /^https?:\/\//i.test(value)
      ? value
      : `${process.env.REACT_APP_BASE_POINT}/${value}`;
  };

  const resetFileInput = () => {
    setValidationError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    if (!filename) {
      resetFileInput();
    }
    setPreviewFailed(false);
  }, [filename, name]);

  const clearFile = () => {
    resetFileInput();
    setFileName("", name);
  };

  const submitFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.target;
    const inputName = inputEl.name;
    const image = inputEl.files?.[0];

    setValidationError("");

    if (!image) return;

    if (validateImage) {
      const { error } = await validateImageFile(
        image,
        effectiveAllowedTypes,
        effectiveMaxFileSize,
      );
      if (error) {
        setValidationError(error);
        notifyError(error);
        inputEl.value = "";
        return;
      }
    }

    const data = new FormData();
    if (domain) data.append("domain", domain);
    data.append("photos", image);
    if (filename) data.append("fileName", filename);

    setIsUploading(true);
    try {
      const response = await postApi({
        api: ADMIN_API.SETTING.FILE_UPLOAD,
        value: data,
      });
      const uploadedPath = response?.data?.data?.path;
      if (!uploadedPath) {
        throw new Error("Upload response did not contain a file path.");
      }
      setFileName(uploadedPath, inputName);
    } catch (err: any) {
      console.log(err);
      inputEl.value = "";
      if (err?.response?.data?.statusCode === 401) {
      } else {
        const message =
          err?.response?.data?.message || "Image upload failed. Please try again.";
        setValidationError(message);
        notifyError(message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const displayUrl = resolveDisplayUrl(filename);
  const acceptAttribute = [
    ...effectiveAllowedTypes,
    ...effectiveAllowedTypes.flatMap((t) =>
      (IMAGE_TYPE_REGISTRY[t]?.extensions || []).map((x) => `.${x}`),
    ),
  ].join(",");

  return (
    <div className={topClass ? topClass : `mb-3 col-3`}>
      <style>{`
        @keyframes img-upload-spin {
          to { transform: rotate(360deg); }
        }
        .img-upload-inline-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 12px;
          padding: 16px;
        }
        .img-upload-spinner {
          box-sizing: border-box;
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
          flex-shrink: 0;
          border-radius: 50%;
          border: 4px solid rgba(0, 0, 0, 0.1);
          border-top-color: ${themeColor || "#667eea"};
          animation: img-upload-spin 0.9s linear infinite;
        }
        .img-upload-text {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          letter-spacing: 0.3px;
        }
      `}</style>

      <div>
        <label
          className="form-label mb_5 d_block"
          style={{ display: "block", marginBottom: "6px" }}
        >
          {label ? label : "Image:"}
        </label>
        <div className="input-group custom-file-button">
          <label className="input-group-text" htmlFor={name}>
            Choose File
          </label>
          <input
            ref={inputRef}
            name={name}
            className="form-control form-control-lg"
            onChange={submitFile}
            id={name}
            type="file"
            accept={acceptAttribute}
            disabled={isUploading}
          />
        </div>
        {props.error && (
          <span className="text-danger d-block mt-1">{props.error}</span>
        )}
        {validationError && (
          <span className="text-danger d-block mt-1">{validationError}</span>
        )}
        {isUploading && (
          <div className="img-upload-inline-loader">
            <span className="img-upload-spinner"></span>
            <span className="img-upload-text">Uploading...</span>
          </div>
        )}
        {filename && !isUploading && (
          <div className="mt-3">
            {previewFailed ? (
              <span className="text-muted d-block">Preview unavailable</span>
            ) : (
              <a href={displayUrl} target="_blank" rel="noreferrer">
                <img
                  style={{ maxWidth: "300px" }}
                  src={displayUrl}
                  alt="Image Preview"
                  onError={() => setPreviewFailed(true)}
                />
              </a>
            )}
            {allowClear && (
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={clearFile}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;